<template>
    <!-- PRODUCT-26110 (INSTANCE_ATTRS_CLASS_STYLE): Vue 3 folds class/style into $attrs
         and stops auto-applying them to root when inheritAttrs is false. The per-field
         `JsonSchemaObjectForm__<name>` class this component sets on each child (see the
         v-for below) therefore stopped reaching a nested object form's root, silently
         un-scoping every `.JsonSchemaObjectForm__<name> ...` rule callers rely on --
         e.g. AddIndividualEntityModal's insuredName title shrink, which regressed to
         Quasar's global `h2` (3.75rem). Bind them to keep the Vue 2 fallthrough. -->
    <div class="JsonSchemaObjectForm" :class="$attrs.class" :style="$attrs.style">
        <div v-if="hasHeader" class="JsonSchemaObjectForm__header">
            <template v-if="form.schema.title">
                <h1 v-if="depth === 0" class="JsonSchemaObjectForm_title">
                    {{ form.schema.title }}
                </h1>
                <h2 v-else-if="depth === 1" class="JsonSchemaObjectForm_title">
                    {{ form.schema.title }}
                </h2>
                <h3 v-else-if="depth === 2" class="JsonSchemaObjectForm_title">
                    {{ form.schema.title }}
                </h3>
                <h4 v-else class="JsonSchemaObjectForm_title">
                    {{ form.schema.title }}
                </h4>
            </template>
            <p v-if="form.schema.description">
                {{ form.schema.description }}
            </p>
        </div>
        <div
            class="JsonSchemaObjectForm__content"
            :class="{'JsonSchemaObjectForm__content--bordered': depth > 0 && hasHeader}"
        >
            <component
                :is="formElementComponent"
                v-for="property in properties"
                :id="`${id}_${property.name}`"
                :key="property.form.key"
                :class="`JsonSchemaObjectForm__${property.name}`"
                :data-test-id="`JsonSchemaObjectForm__${property.name}`"
                :depth="depth + 1"
                :form="property.form"
                :required="property.required"
                :disabled="disabled || property.form.schema.readOnly"
                :form-element-component="formElementComponent"
                :custom-component-input="customComponentInput"
                :value="getPropValue(property)"
                :touched="childTouchState[property.name] || touched"
                :no-local-touch="noLocalTouch"
                :strings="strings"
                :deletable="property.form.type === 'object' && !property.required"
                :rbac-disabled="rbacDisabled"
                :wrap-value-mode="wrapValueMode"
                v-bind="$attrs"
                @input="onInput(property, $event)"
                @change="onChange(property, $event)"
                @touch="updateTouchState(property.name)"
            />
            <div v-if="deletable" class="JsonSchemaObjectForm__deleteIconContainer">
                <NewButton
                    variant="flat"
                    size="icon-sm"
                    color="secondary"
                    class="JsonSchemaObjectForm__deleteIcon JsonSchemaForm__deleteIcon"
                    @click="$emit('delete')"
                >
                    <FontAwesomeIcon :icon="faXmark" />
                </NewButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component } from 'vue-property-decorator';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonFormObject, JsonFormProperty } from '@evidentid/json-schema/interfaces/JsonForm';

    import NewButton from '../../new-button/NewButton.vue';

    import AbstractJsonSchemaForm from './AbstractJsonSchemaForm';

    @Component({
        emits: [ 'change', 'delete', 'input' ],
        inheritAttrs: false,
        components: {
            FontAwesomeIcon,
            NewButton,
        },
    })
    export default class JsonSchemaObjectForm extends AbstractJsonSchemaForm<JsonFormObject, object> {
        private faXmark = faXmark;
        private childTouchState: Record<string, boolean> = {};

        private get properties() {
            return this.form.getProperties(this.value);
        }

        private get hasHeader() {
            return this.form.schema.title || this.form.schema.description;
        }

        private updateTouchState(propName: string) {
            if (!this.noLocalTouch || this.touched) {
                this.childTouchState[propName] = true;
            }
        }

        private getPropValue(property: JsonFormProperty): unknown {
            return property.form.getValue((this.value as any)?.[property.name] ?? undefined, true);
        }

        private onInput(property: JsonFormProperty, value: any) {
            this.$emit('input', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }

        private onChange(property: JsonFormProperty, value: any) {
            this.$emit('change', this.form.getValue({
                ...this.value,
                [property.name]: value,
            }, true));
        }
    }
</script>
